import type { CSSProperties } from "react";

import styles from "./Avatar.module.css";
import { cx } from "../lib/cx";

export type AvatarProps = {
  readonly className?: string;
  readonly initials?: string;
  readonly name: string;
  readonly ring?: boolean;
  readonly size?: number;
  readonly style?: CSSProperties;
};

const avHash = (s: string): number => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// Low-chroma surface tints paired with a matching darker ink for the initials.
const TONES: ReadonlyArray<readonly [string, string]> = [
  ["#efedf4", "#4a4557"],
  ["#eceae4", "#514c42"],
  ["#e6ebe8", "#3f4a44"],
  ["#eae9ee", "#474655"],
];

const NAMES: Record<string, string> = {
  AR: "Priya Shah",
  JD: "Jane Diaz",
  MK: "Marcus Okafor",
  MO: "Marcus Okafor",
};

// Flat, monochrome, bordered circle with ink initials. Distinction comes from
// the initials plus a faint deterministic per-person tint — no rainbow.
export const Avatar = ({
  className,
  initials,
  name,
  ring = false,
  size = 24,
  style,
}: AvatarProps) => {
  const display = NAMES[name] ?? name;
  const displayedInitials =
    initials ??
    display
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  const tone = TONES[avHash(display) % TONES.length] ?? TONES[0]!;

  return (
    <div
      aria-label={display}
      className={cx(styles.avatar, ring && styles.ring, className)}
      style={{
        background: tone[0],
        color: tone[1],
        fontSize: Math.round(size * 0.4),
        height: size,
        width: size,
        ...style,
      }}
    >
      {displayedInitials}
    </div>
  );
};
